import { useEffect, useRef, useState } from 'react';
import { CpmBadge, CpmButton, CpmFilterDropdown } from '../../../components/design-system';
import {
    copyTemplateFromStoreBrand,
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
    listStates,
    listStoreBrands,
} from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    FormPhase,
    StoreBrandListRow,
    TemplateDetails,
} from '../../../types/reportsAdmin.types';
import { pushDropdownCloser } from '../../../utils/dropdownEscapeStack';
import { REPORTS_COPY } from '../copy';
import {
    ReportsAlert,
    ReportsDialog,
    ReportsDialogBody,
    ReportsDialogFooter,
    ReportsDialogHeader,
    ReportsFieldLabel,
    ReportsInput,
} from '../components/ReportsDialog';
import InfiniteScrollSentinel from '../components/InfiniteScrollSentinel';
import SearchField from '../components/SearchField';
import { plural, stateName } from '../helpers';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

interface CopyFromStoreBrandModalProps {
    onClose: () => void;
    onCreated: (row: TemplateDetails) => void;
}

function onCount(row: StoreBrandListRow): number {
    return typeof row.enabledCount === 'number' ? row.enabledCount : 0;
}

function brandStateLabel(row: StoreBrandListRow): string {
    return row.state ?? row.mixedStates?.join(', ') ?? '—';
}

export default function CopyFromStoreBrandModal({
    onClose,
    onCreated,
}: CopyFromStoreBrandModalProps) {
    const [search, setSearch] = useState('');
    const debounced = useDebouncedValue(search, 420);
    const [states, setStates] = useState<string[]>([]);
    const [stateDocs, setStateDocs] = useState<CpmState[]>([]);
    const [statesFailed, setStatesFailed] = useState(false);
    const [searchRetry, setSearchRetry] = useState(0);
    const [hits, setHits] = useState<StoreBrandListRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [picked, setPicked] = useState<StoreBrandListRow | null>(null);
    const [name, setName] = useState('');
    const [phase, setPhase] = useState<FormPhase>('idle');
    const [writeError, setWriteError] = useState('');
    const [listError, setListError] = useState('');
    const [searchingLabel, setSearchingLabel] = useState<string | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [moreBusy, setMoreBusy] = useState(false);
    const [moreFailed, setMoreFailed] = useState(false);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const searchWrapRef = useRef<HTMLDivElement>(null);

    const query = debounced.trim();
    const hasQuery = query.length >= 2;
    const statesKey = states.join('|');
    const anyFilters = Boolean(search.trim() || states.length);
    const locked = phase === 'saving';
    const searching = Boolean(searchingLabel);
    const paint = hasQuery && !searching;
    const filteredEmpty = paint && hits.length === 0;
    const hasRows = paint && hits.length > 0;
    const moreLeft = Math.max(0, total - hits.length);
    const atEnd = hasRows && moreLeft === 0;

    const matchLine = hasQuery
        ? `${total}${total === 1 ? ' store brand matches “' : ' store brands match “'}${search.trim()}”${
              states.length ? ` in ${states.join(', ')}` : ''
          }${moreLeft > 0 ? ` · showing ${hits.length}, more matches after that` : ''}`
        : '';

    const filteredEmptyDetail = `Search “${search.trim()}”${
        states.length ? ` in ${states.join(', ')}` : ''
    }. Store brands exist — this search just didn’t match them.`;

    const loadStates = () => {
        setStatesFailed(false);
        void listStates()
            .then((docs) => {
                setStateDocs(docs);
                setStatesFailed(false);
            })
            .catch(() => {
                setStatesFailed(true);
            });
    };

    useEffect(() => {
        loadStates();
    }, []);

    useEffect(() => {
        if (!hasQuery) {
            setHits([]);
            setTotal(0);
            setPage(1);
            setSearchingLabel(null);
            setMoreBusy(false);
            setMoreFailed(false);
            return;
        }
        let cancelled = false;
        setSearchingLabel((prev) => prev ?? 'Getting the store brands that match that search.');
        void listStoreBrands({
            search: query,
            states: states.length ? states : undefined,
            page: 1,
            limit: 25,
        })
            .then((data) => {
                if (cancelled) return;
                setListError('');
                setMoreFailed(false);
                setHits(data.storeBrands);
                setTotal(data.total);
                setPage(1);
            })
            .catch((err) => {
                if (cancelled) return;
                setListError(getReportsAdminErrorMessage(err));
                setHits([]);
                setTotal(0);
            })
            .finally(() => {
                if (!cancelled) setSearchingLabel(null);
            });
        return () => {
            cancelled = true;
        };
        // statesKey keeps the filter identity stable — do not depend on the array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, hasQuery, statesKey, searchRetry]);

    useEffect(() => {
        if (!menuOpen || locked) return;
        const pop = pushDropdownCloser(() => setMenuOpen(false));
        const onDoc = (e: MouseEvent) => {
            if (toolbarRef.current?.contains(e.target as Node)) return;
            if (searchWrapRef.current?.contains(e.target as Node)) return;
            setMenuOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => {
            pop();
            document.removeEventListener('mousedown', onDoc);
        };
    }, [menuOpen, locked]);

    const persist = async () => {
        if (!picked || !name.trim()) {
            setWriteError('Pick a store brand and type a template name.');
            return;
        }
        setPhase('saving');
        setWriteError('');
        try {
            const brandStates = picked.state
                ? [picked.state]
                : (picked.mixedStates ?? []).filter(Boolean);
            const row = await copyTemplateFromStoreBrand({
                storeBrandID: picked.storeBrandId,
                name: name.trim(),
                tags: ['Copied'],
                states: brandStates,
                allStates: brandStates.length === 0,
                activeStatus: 'Active',
            });
            onCreated(row);
        } catch (err) {
            setWriteError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    const clearFilters = () => {
        setSearch('');
        setStates([]);
        setHits([]);
        setTotal(0);
        setPage(1);
        setSearchingLabel(null);
    };

    const loadMore = () => {
        if (locked || !hasQuery || moreLeft === 0 || moreBusy) return;
        setMoreBusy(true);
        setMoreFailed(false);
        const nextPage = page + 1;
        void listStoreBrands({
            search: query,
            states: states.length ? states : undefined,
            page: nextPage,
            limit: 25,
        })
            .then((data) => {
                setHits((prev) => [...prev, ...data.storeBrands]);
                setTotal(data.total);
                setPage(nextPage);
            })
            .catch(() => {
                setMoreFailed(true);
            })
            .finally(() => {
                setMoreBusy(false);
            });
    };

    const pickRow = (row: StoreBrandListRow) => {
        setPicked(row);
        setName(`${row.name} — copied`);
        setMenuOpen(false);
        setWriteError('');
        setListError('');
    };

    const rowBadge = (row: StoreBrandListRow) => {
        const n = onCount(row);
        if (picked?.storeBrandId === row.storeBrandId) {
            return { badge: 'Selected', tone: 'brand' as const };
        }
        if (n === 0) return { badge: 'None on yet', tone: 'warning' as const };
        return { badge: 'Copy from this', tone: 'success' as const };
    };

    const pickedNote = picked
        ? `${plural(onCount(picked), 'Shop-can-download report is', 'Shop-can-download reports are')} on for this store brand right now, in ${brandStateLabel(picked)}. Those start in the new template. Always on and Doesn’t run yet are never adds. Zero is a number — a store brand with nothing on still creates the template.`
        : '';

    const footerNote = !picked
        ? 'Type a store brand name to find one. Getting into Templates never needed one.'
        : !name.trim()
          ? 'Confirm needs a name no other template holds.'
          : 'Confirm creates the template. Copying is not applying — nothing is turned on or off for that store brand.';

    return (
        <ReportsDialog
            open
            width={740}
            zIndex={320}
            height="min(560px, 100%)"
            onClose={locked ? () => undefined : onClose}
            closeOnBackdrop={!locked}
            closeOnEscape={!locked}
        >
            <ReportsDialogHeader
                kicker="Copy from a store brand already using reports"
                title="Copy one store brand's on reports into a new template"
                subtitle="The new template gets the report ids that store brand has on right now. Nothing is turned on or off for that store brand, and the store brand is not linked to the template."
                onClose={locked ? undefined : onClose}
            />
            <ReportsDialogBody pad="picker">
                {listError ? (
                    <div style={{ marginBottom: 14 }}>
                        <ReportsAlert
                            tone="danger"
                            title="Couldn't load the store brands"
                            body="The store brands you can copy from did not come back, so this is not an empty system and no template has been created. The template list behind this dialog is untouched, and getting into Templates never needed a store brand."
                            reason={reportsAdminErrorReason(listError)}
                            action={
                                <CpmButton
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSearchRetry((n) => n + 1)}
                                >
                                    Retry the store brands
                                </CpmButton>
                            }
                        />
                    </div>
                ) : null}

                <div ref={toolbarRef} className="flex items-center flex-wrap" style={{ gap: 9 }}>
                    <div ref={searchWrapRef} className="relative" style={{ width: 320 }}>
                        <SearchField
                            value={search}
                            onChange={(next) => {
                                setSearch(next);
                                setMenuOpen(true);
                                if (next.trim().length < 2) {
                                    setSearchingLabel(null);
                                } else {
                                    setSearchingLabel(
                                        'Searching the store brands. The list is not ready yet — blank here is not a count of zero.'
                                    );
                                }
                            }}
                            onFocus={() => {
                                if (!locked) setMenuOpen(true);
                            }}
                            placeholder="Search a store brand by name"
                            disabled={locked}
                            width={320}
                            size="sm"
                        />
                        {menuOpen && !locked ? (
                            <div
                                className="bg-white"
                                style={{
                                    position: 'absolute',
                                    top: 42,
                                    left: 0,
                                    zIndex: 150,
                                    width: 420,
                                    maxWidth: '60vw',
                                    maxHeight: 216,
                                    overflow: 'hidden',
                                    padding: 6,
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 'var(--cpm-radius-xl)',
                                    boxShadow: 'var(--cpm-shadow-popover-deep)',
                                }}
                            >
                                {searching ? (
                                    <div
                                        className="flex items-center"
                                        style={{ gap: 9, padding: '10px 10px 12px' }}
                                    >
                                        <span className="cpm-pulse-dot" />
                                        <span
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                lineHeight: 1.45,
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            {searchingLabel}
                                        </span>
                                    </div>
                                ) : (
                                    <div>
                                        {hasQuery ? (
                                            <div
                                                style={{
                                                    padding: '7px 10px 8px',
                                                    fontSize: 10.5,
                                                    fontWeight: 800,
                                                    letterSpacing: '0.04em',
                                                    color: 'var(--cpm-text-tertiary)',
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}
                                            >
                                                {matchLine}
                                            </div>
                                        ) : null}

                                        {filteredEmpty ? (
                                            <div style={{ padding: '4px 10px 12px' }}>
                                                <div
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                        color: 'var(--cpm-text-primary)',
                                                    }}
                                                >
                                                    No store brand matches that search
                                                </div>
                                                <div
                                                    style={{
                                                        marginTop: 4,
                                                        fontSize: 11.5,
                                                        fontWeight: 500,
                                                        lineHeight: 1.45,
                                                        color: 'var(--cpm-text-secondary)',
                                                    }}
                                                >
                                                    The search and filters caused this.{' '}
                                                    {filteredEmptyDetail}
                                                </div>
                                                <div style={{ marginTop: 10 }}>
                                                    <CpmButton
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={clearFilters}
                                                    >
                                                        {REPORTS_COPY.CLEAR_SEARCH_FILTERS}
                                                    </CpmButton>
                                                </div>
                                            </div>
                                        ) : null}

                                        {hasRows ? (
                                            <>
                                                <div
                                                    className="overflow-y-auto [overscroll-behavior:contain]"
                                                    style={{ maxHeight: 132 }}
                                                >
                                                    {hits.map((row) => {
                                                        const meta = rowBadge(row);
                                                        const n = onCount(row);
                                                        return (
                                                            <button
                                                                key={row.storeBrandId}
                                                                type="button"
                                                                onClick={() => pickRow(row)}
                                                                onMouseEnter={() =>
                                                                    setHoveredId(row.storeBrandId)
                                                                }
                                                                onMouseLeave={() =>
                                                                    setHoveredId(null)
                                                                }
                                                                className="flex w-full items-center justify-between text-left"
                                                                style={{
                                                                    gap: 12,
                                                                    padding: '9px 10px',
                                                                    border: 0,
                                                                    borderRadius: 8,
                                                                    background:
                                                                        hoveredId ===
                                                                        row.storeBrandId
                                                                            ? 'var(--cpm-purple-tint-3)'
                                                                            : 'transparent',
                                                                    cursor: 'pointer',
                                                                    transition:
                                                                        'background 120ms ease',
                                                                }}
                                                            >
                                                                <span className="min-w-0">
                                                                    <span
                                                                        className="block"
                                                                        style={{
                                                                            fontSize: 12.5,
                                                                            fontWeight: 800,
                                                                            letterSpacing:
                                                                                '-0.01em',
                                                                            color: 'var(--cpm-text-primary)',
                                                                        }}
                                                                    >
                                                                        {row.name}
                                                                    </span>
                                                                    <span
                                                                        className="block"
                                                                        style={{
                                                                            marginTop: 2,
                                                                            fontSize: 11,
                                                                            fontWeight: 600,
                                                                            color: 'var(--cpm-text-tertiary)',
                                                                        }}
                                                                    >
                                                                        {brandStateLabel(row)} ·{' '}
                                                                        {plural(
                                                                            n,
                                                                            'report on',
                                                                            'reports on'
                                                                        )}
                                                                    </span>
                                                                </span>
                                                                <CpmBadge tone={meta.tone}>
                                                                    {meta.badge}
                                                                </CpmBadge>
                                                            </button>
                                                        );
                                                    })}
                                                    <InfiniteScrollSentinel
                                                        hasMore={moreLeft > 0}
                                                        loading={moreBusy}
                                                        error={moreFailed}
                                                        disabled={locked}
                                                        onLoadMore={loadMore}
                                                        showEnd={atEnd}
                                                        endVariant="rule"
                                                        compact
                                                        loadingLabel="Getting the next 25 store brands."
                                                    />
                                                </div>
                                            </>
                                        ) : null}

                                        {!hasQuery ? (
                                            <div style={{ padding: '8px 10px 12px' }}>
                                                <div
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                        color: 'var(--cpm-text-primary)',
                                                    }}
                                                >
                                                    Type a store brand name
                                                </div>
                                                <div
                                                    style={{
                                                        marginTop: 4,
                                                        fontSize: 11.5,
                                                        fontWeight: 500,
                                                        lineHeight: 1.45,
                                                        color: 'var(--cpm-text-secondary)',
                                                    }}
                                                >
                                                    Two letters or more and the matches appear here.
                                                    Nothing is listed before you type.
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>

                    <CpmFilterDropdown
                        label="State"
                        triggerLabel={states.length ? `State · ${states.length}` : 'State'}
                        triggerVariant="chip"
                        portal
                        menuMinWidth={286}
                        includeEmptyOption={false}
                        disabled={locked}
                        failed={statesFailed}
                        failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                        failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                        onRetry={loadStates}
                        options={stateDocs
                            .map(stateName)
                            .filter(Boolean)
                            .map((item) => ({ value: item, label: item }))}
                        selected={states}
                        onChange={(next) => {
                            setStates(next);
                            if (query.length >= 2) {
                                setSearchingLabel('Applying the state filter to what you typed.');
                            }
                        }}
                    />

                    {anyFilters ? (
                        <CpmButton
                            variant="ghost"
                            size="sm"
                            disabled={locked}
                            onClick={clearFilters}
                        >
                            {REPORTS_COPY.CLEAR_SEARCH_FILTERS}
                        </CpmButton>
                    ) : null}
                </div>

                {!picked ? (
                    <div
                        style={{
                            marginTop: 12,
                            fontSize: 11.5,
                            fontWeight: 600,
                            lineHeight: 1.45,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        Search a store brand by name and pick it from the list. Nothing is listed
                        until you type — there are hundreds of store brands.
                    </div>
                ) : (
                    <div
                        style={{
                            marginTop: 14,
                            padding: '16px 18px',
                            border: '1px solid var(--cpm-border-default)',
                            borderRadius: 10,
                            background: 'var(--cpm-surface-subtle)',
                        }}
                    >
                        <div className="flex items-start justify-between" style={{ gap: 14 }}>
                            <div className="min-w-0">
                                <div
                                    className="uppercase"
                                    style={{
                                        fontSize: 9.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                        color: 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    {REPORTS_COPY.SELECTED_STORE_BRAND}
                                </div>
                                <div
                                    style={{
                                        marginTop: 5,
                                        fontSize: 14.5,
                                        fontWeight: 800,
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {picked.name}
                                </div>
                            </div>
                            <CpmButton
                                variant="ghost"
                                size="sm"
                                disabled={locked}
                                onClick={() => {
                                    setSearch('');
                                    setHits([]);
                                    setTotal(0);
                                    setPage(1);
                                    setSearchingLabel(null);
                                    setWriteError('');
                                    setMenuOpen(true);
                                }}
                            >
                                Change the store brand
                            </CpmButton>
                        </div>
                        <div
                            style={{
                                marginTop: 6,
                                fontSize: 12,
                                fontWeight: 500,
                                lineHeight: 1.5,
                                color: 'var(--cpm-text-secondary)',
                            }}
                        >
                            {pickedNote}
                        </div>
                        <div style={{ marginTop: 14, maxWidth: 420 }}>
                            <ReportsFieldLabel required>
                                {REPORTS_COPY.NEW_TEMPLATE_NAME}
                            </ReportsFieldLabel>
                            <ReportsInput
                                value={name}
                                disabled={locked}
                                placeholder="Name the template this creates"
                                onChange={(e) => setName(e.target.value)}
                            />
                            <div
                                style={{
                                    marginTop: 6,
                                    fontSize: 11.5,
                                    fontWeight: 500,
                                    lineHeight: 1.45,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                The template needs its own name — the store brand it came from is
                                not stored on it.
                            </div>
                        </div>
                        {writeError ? (
                            <div style={{ marginTop: 14 }}>
                                <ReportsAlert
                                    tone="danger"
                                    title="Couldn't copy the template"
                                    body="No template was created and the store brand you picked is still selected. That store brand was not changed either way — copying never turns a report on or off."
                                    reason={reportsAdminErrorReason(
                                        writeError,
                                        'No template was created and the store brand you picked is still selected.'
                                    )}
                                    action={
                                        <CpmButton
                                            variant="outline"
                                            size="sm"
                                            onClick={() => void persist()}
                                        >
                                            Retry the copy
                                        </CpmButton>
                                    }
                                />
                            </div>
                        ) : null}
                    </div>
                )}
            </ReportsDialogBody>
            <ReportsDialogFooter
                note={footerNote}
                saving={locked}
                savingLabel="Creating the template from that store brand's on reports. The write has not finished — do not fire it again."
                onCancel={onClose}
                primaryLabel={REPORTS_COPY.CONFIRM}
                onPrimary={() => void persist()}
                primaryDisabled={!picked || !name.trim()}
            />
        </ReportsDialog>
    );
}
